import { PriceHover, PriceHoverData } from '@chart';
import {
  ChartTooltip,
  ChartTooltipItem,
  ChartTooltipLabel,
} from '@design/charts/chart-tooltip/ChartTooltip';
import { TooltipSortConfig } from '@terminal/modules/multi-line-chart/MultiLineChartModule';
import { formatUtcDate } from '@terminal/services/insights/utils/insight-utils';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { CSSProperties, forwardRef } from 'react';

import styles from './SimpleChartTooltip.module.scss';

export interface SimpleChartTooltipProps {
  priceHover?: PriceHover;
  styles?: CSSProperties;
  sortConfig?: TooltipSortConfig;
}

export const SimpleChartTooltip = forwardRef<
  HTMLDivElement,
  SimpleChartTooltipProps
>(({ priceHover, styles: stylesProp, sortConfig }, ref) => {
  const { colors } = useChartTheme();

  if (!priceHover) {
    return null;
  }

  const parseFieldValue = (
    item: PriceHoverData,
    sortConfig: TooltipSortConfig
  ) => {
    const value = item[sortConfig.field];

    if (typeof value === 'string') {
      return parseFloat(value.replace(',', ''));
    }

    return 0;
  };

  const pane = priceHover.panes[0];

  if (sortConfig) {
    pane.data.sort((a, b) => {
      const valueA = parseFieldValue(a, sortConfig);
      const valueB = parseFieldValue(b, sortConfig);
      return sortConfig.desc ? valueB - valueA : valueA - valueB;
    });
  }

  const isoDate = formatUtcDate(priceHover.date.toISOString(), 'MMM d yyyy');

  return (
    <ChartTooltip
      ref={ref}
      title={isoDate}
      style={{
        zIndex: 'var(--z-tooltip)',
        ...stylesProp,
      }}
      data-testid="simple-chart-tooltip"
    >
      {pane.data.map(data => (
        <ChartTooltipItem key={data.chartAssetData.entity.tag}>
          <ChartTooltipLabel>
            <span
              className={styles.dot}
              style={{
                backgroundColor: colors.getThemeColor(
                  data.chartAssetData.lineColorToken
                ),
              }}
            />
            {data.chartAssetData.entity.formattedTicker}
          </ChartTooltipLabel>
          <ChartTooltipLabel data-testid="tooltip-price-label">
            {data.priceFormatted}
          </ChartTooltipLabel>
        </ChartTooltipItem>
      ))}
    </ChartTooltip>
  );
});
